import useSWR from 'swr';
import { contractService } from '../services/contractService';
import { userService } from '../services/userService';
import { ContractWithUsers } from '../types/contract';
import { resolveAvatarUrl } from '../constants/assets';

type ViewMode = 'client' | 'creator';

interface ContractsData {
  contracts: ContractWithUsers[];
  currentUserId: string;
}

async function fetchContracts(viewMode: ViewMode): Promise<ContractsData> {
  const userRes = await userService.getUserProfile();
  let userId = '';
  if (userRes.success && userRes.data) {
    userId = userRes.data.userProfile.userId;
  }

  if (!userId) return { contracts: [], currentUserId: '' };

  const filter = viewMode === 'client'
    ? { clientUserId: userId }
    : { creatorUserId: userId };

  const res = await contractService.listContracts(filter);
  if (!res.success || !res.data) return { contracts: [], currentUserId: userId };

  const rawContracts = res.data.contracts;

  const userIdsToFetch = new Set<string>();
  rawContracts.forEach((c: any) => {
    if (c.client_user_id && c.client_user_id !== userId) userIdsToFetch.add(c.client_user_id);
    if (c.creator_user_id && c.creator_user_id !== userId) userIdsToFetch.add(c.creator_user_id);
  });

  let avatarsMap: Record<string, { avatarUrl: string; nickname: string }> = {};
  if (userIdsToFetch.size > 0) {
    const avatarRes = await userService.getAvatarsBatch(Array.from(userIdsToFetch));
    if (avatarRes.success && avatarRes.data?.avatars) {
      avatarsMap = avatarRes.data.avatars;
    }
  }

  const contracts: ContractWithUsers[] = rawContracts.map((c: any) => {
    const clientIsMe = c.client_user_id === userId;
    const creatorIsMe = c.creator_user_id === userId;

    const clientAvatarInfo = clientIsMe && userRes.data
      ? { avatarUrl: userRes.data.userProfile.avatarUrl, nickname: userRes.data.userProfile.displayName || userRes.data.userProfile.username }
      : (avatarsMap[c.client_user_id] || { avatarUrl: '', nickname: 'Client User' });

    const creatorAvatarInfo = creatorIsMe && userRes.data
      ? { avatarUrl: userRes.data.userProfile.avatarUrl, nickname: userRes.data.userProfile.displayName || userRes.data.userProfile.username }
      : (avatarsMap[c.creator_user_id] || { avatarUrl: '', nickname: 'Creator User' });

    return {
      ...c,
      client: { userId: c.client_user_id, nickname: clientAvatarInfo.nickname, avatar: resolveAvatarUrl(clientAvatarInfo.avatarUrl), role: 'client' },
      creator: { userId: c.creator_user_id, nickname: creatorAvatarInfo.nickname, avatar: resolveAvatarUrl(creatorAvatarInfo.avatarUrl), role: 'creator' }
    };
  });

  return { contracts, currentUserId: userId };
}

export function useContracts(viewMode: ViewMode) {
  const { data, error, isLoading, mutate } = useSWR<ContractsData>(
    `contracts-${viewMode}`,
    () => fetchContracts(viewMode),
    {
      revalidateOnFocus: false,
      dedupingInterval: 10000, // dedup requests within 10s
    }
  );

  return {
    contracts: data?.contracts || [],
    currentUserId: data?.currentUserId || '',
    loading: isLoading,
    error,
    refresh: mutate,
  };
}
